import React, { useState } from 'react'
import { ProductCardProps } from './CategoryComponent';

type CategoryVerticalCardProps = ProductCardProps & {
  id?: string;
  showButtonText?: boolean;
  showButtonIcon?: boolean;
  buttonIconUrl?: string;
  buttonPlacement?: 'Left' | 'Centre' | 'Right';
};

const CategoryVerticalCard = ({ id, product, buttonLabel = 'Add to Build', showButtonText = true, showPrice = true, showAvailabilityStatus = true, inStockLabel = 'In Stock', backOrderLabel = 'Out of Stock', descriptionStyle = 'Full', showProductDescription = true, showCategory = false, showModelNumber = false, showButtonIcon = false, buttonIconUrl = '', buttonPlacement = 'Left'}: CategoryVerticalCardProps) => {
  const baseId = id || 'category-vertical-card';
  const [isExpanded, setIsExpanded] = useState(false);
  const inStock = true;
  const price = typeof product.min_ad_price === 'string'
    ? parseFloat(product.min_ad_price) || 0
    : product.min_ad_price || 0;

    const renderDescription = () => {
        if (!showProductDescription) return null;

        const description = product.description || '';

        switch (descriptionStyle) {
            case 'Partly':
                const truncatedText = description.length > 100
                    ? description.substring(0, 100) + '...'
                    : description;
                return (
                    <p id={`${baseId}-description`} className="tw-text-[#172b4d] tw-text-[10px] tw-font-normal">
                        {truncatedText}
                    </p>
                );

            case 'Full':
                return (
                    <p id={`${baseId}-description`} className="tw-text-[#172b4d] tw-text-[10px] tw-font-normal">
                        {description}
                    </p>
                );

            case 'Collapsed':
                return (
                    <div id={`${baseId}-description-wrapper`}>
                        <p id={`${baseId}-description`} className="tw-text-[#172b4d] tw-text-[10px] tw-font-normal">
                            {isExpanded ? description : description.substring(0, 50) + '...'}
                        </p>
                        {description.length > 50 && (
                            <button
                                id={`${baseId}-expand-btn`}
                                data-testid={`${baseId}-expand-btn`}
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="tw-text-[#0052cc] tw-text-[10px] tw-font-medium hover:tw-text-[#0065ff] tw-mt-0.5"
                            >
                                {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                        )}
                    </div>
                );

            default:
                return (
                    <p id={`${baseId}-description`} className="tw-text-[#172b4d] tw-text-[10px] tw-font-normal">
                        {description}
                    </p>
                );
        }
    };

    const renderButton = (buttonClass: string, textSize: string = 'tw-text-[10px]') => {
        const hasIcon = showButtonIcon && buttonIconUrl;
        const hasText = showButtonText;

        return (
            <button
                id={`${baseId}-add-btn`}
                data-testid={`${baseId}-add-btn`}
                className={`tw-w-full tw-border tw-border-[#6d788c] tw-text-[#172b4d] tw-font-medium tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-gap-1.5 ${buttonClass}`}
            >
                {/* Icon on the left */}
                {hasIcon && buttonPlacement === 'Left' && (
                    <img id={`${baseId}-icon-left`} src={buttonIconUrl} alt="Icon" className="tw-w-4 tw-h-4" />
                )}

                {/* Text (show only if showButtonText is true) */}
                {hasText && <span className={textSize}>{buttonLabel}</span>}

                {/* Icon on the right or center */}
                {hasIcon && (buttonPlacement === 'Right' || buttonPlacement === 'Centre') && (
                    <img id={`${baseId}-icon-right`} src={buttonIconUrl} alt="Icon" className="tw-w-4 tw-h-4" />
                )}
            </button>
        );
    };  
  
  return (
    <article id={baseId} data-testid={baseId} className="tw-p-2 tw-bg-[#f4f5f7] tw-border tw-border-[#e6e7e9] tw-w-full tw-max-w-[165px]">
      <div id={`${baseId}-image`} className="tw-w-full tw-h-[85px] tw-bg-[#e6e7e9] tw-mb-2" />
      {showModelNumber && product?.model_number ? (
          <p id={`${baseId}-model`} className="tw-text-[#6D788C] tw-text-[10px] tw-font-normal tw-mb-0.5">Model: {product.model_number}</p>
      ): null}
      <h4 id={`${baseId}-name`} className="tw-text-[#172b4d] tw-text-xs tw-font-semibold tw-mb-0.5">{product.name}</h4>
      {renderDescription()}
      {showCategory ? (
                <p id={`${baseId}-category`} className="tw-text-[#6D788C] tw-text-[10px] tw-font-normal tw-mt-1 tw-mb-1">{product?.category ?? ''}</p>
            ): null}

      {showPrice && (
        <div id={`${baseId}-price`} className="tw-text-[#172b4d] tw-text-sm tw-font-semibold tw-mb-3">$ {price.toFixed(2)}</div>
      )}

      {showAvailabilityStatus && (
        <div id={`${baseId}-availability`} className="tw-flex tw-items-center tw-mb-2">
          <div id={`${baseId}-availability-icon`} className={`tw-w-3.5 tw-h-3.5 tw-rounded-full tw-border tw-border-[#6d788c] tw-flex tw-items-center tw-justify-center ${inStock ? 'tw-bg-[#e6f7ff]' : 'tw-bg-[#f4f5f7]'}`}>
            {inStock && (
              <svg id={`${baseId}-check-icon`} xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
                <path d="M7 1L2.875 5L1 3.18182" stroke="#6D788C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span id={`${baseId}-availability-label`} className="tw-ml-2 tw-text-[#172b4d] tw-text-[10px] tw-font-semibold">
            {inStock ? inStockLabel : backOrderLabel}
          </span>
        </div>
      )}

      {renderButton('tw-py-1 tw-px-2 tw-text-[10px]', 'tw-text-[10px]')}
    </article>
  );
};

export default CategoryVerticalCard;